import axios from 'axios'

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor to add auth token and impersonation header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('app_auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add impersonation header if active
    try {
      const impersonation = localStorage.getItem('app_impersonation')
      if (impersonation) {
        const { orgId } = JSON.parse(impersonation)
        if (orgId) {
          config.headers['X-Impersonate-Org'] = orgId
        }
      }
    } catch {
      // ignore parse errors
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Lightweight publisher so useFeatureGate can subscribe without importing api
// (which would create a circular import via the FeatureGateProvider chain).
type UpgradePrompt = { feature: string; requiredTier: string }
const upgradeListeners = new Set<(p: UpgradePrompt) => void>()
export function onFeatureLocked(listener: (p: UpgradePrompt) => void) {
  upgradeListeners.add(listener)
  return () => upgradeListeners.delete(listener)
}

// Response interceptor to handle auth errors + feature-lock 402s
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || ''
      // Never auto-redirect on 401 — components need to surface the error.
      // A 401 from /api/auth/login just means wrong credentials, not "log out".
      // Previously this reloaded the page and wiped the "Invalid email or password"
      // toast before the user could read it.
      console.warn('[API] Received 401 for endpoint:', requestUrl)
    }
    // 402 Payment Required with feature_locked body → notify the feature-gate
    // provider to pop the upgrade modal. Any endpoint protected by
    // requireFeature() will use this shape.
    if (error.response?.status === 402 && error.response?.data?.error === 'feature_locked') {
      const { feature, requiredTier } = error.response.data
      if (feature && requiredTier) {
        upgradeListeners.forEach((fn) => {
          try { fn({ feature, requiredTier }) } catch { /* ignore listener errors */ }
        })
      }
    }
    return Promise.reject(error)
  }
)

export default api